import { useReducer, useEffect, useState, useCallback } from 'react'
import { reducer } from './reducer'
import * as ACT from './actions'
import { FollowupModal } from './FollowupModal'
import { makeGetRequest, makePostRequest, makePutRequest } from 'utils/api'
import {
  fromTemplates,
  toContactsData,
  toContactEmail,
  toDealsFollowupData,
  toUsers
} from './serializers'

const initialState = {
  deals: [],
  selectedTemplate: null,
  templates: [],
  contacts: [],
  ccContacts: []
}

const FollowupModalContainer = ({ showFollowupModal, toggleFollowupModal, selectedCards }) => {
  const [allContactsSelected, setAllContactsSelected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [dealRemoved, setDealRemoved] = useState(false)
  const [users, setUsers] = useState([])

  const [state, dispatch] = useReducer(reducer, initialState)
  const { deals, selectedTemplate, templates, contacts } = state

  useEffect(() => {
    dispatch({
      type: ACT.SET_DEALS,
      payload: {
        deals: selectedCards
      }
    })
  }, [dispatch, selectedCards])

  useEffect(() => {
    if (users.length) return
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeDeal = id => {
    dispatch({
      type: ACT.REMOVE_DEAL,
      payload: {
        id
      }
    })
    setDealRemoved(true)
  }

  const getDealsContacts = useCallback(
    async cb => {
      const dealIds = deals.map(deal => deal.id).join(',')
      const url = `/v1/deals/contactsForDeals?allContacts=${allContactsSelected}&dealIds=${dealIds}`
      const contacts = await makeGetRequest(url)

      dispatch({
        type: ACT.SET_CONTACTS,
        payload: {
          contacts: toContactsData(contacts)
        }
      })

      cb && cb()
    },
    [allContactsSelected, deals]
  )

  useEffect(() => {
    ;(async () => {
      const templates = await makeGetRequest('/v1/email/templates/followup')
      dispatch({
        type: ACT.SET_TEMPLATES,
        payload: {
          templates: fromTemplates(templates)
        }
      })
    })()
  }, [dispatch])

  const changeTemplate = useCallback(
    async selectedTemplate => {
      const templateBody = await makePostRequest(
        '/v1/email/templates/contacts/withselecteddeals',
        state.contacts.map(contact => ({
          contactId: contact.contactId,
          deals: [
            ...contact.dealsToFollowup.filter(d => d.isChecked).map(d => d.id),
            ...contact.otherDealsToFollowUp.filter(d => d.isChecked).map(d => d.id)
          ],
          templateId: selectedTemplate.value
        }))
      )
      dispatch({
        type: ACT.SELECT_GLOBAL_TEMPLATE,
        payload: {
          selectedTemplate,
          templateBody
        }
      })
    },
    [state.contacts]
  )

  const getFollowupData = useCallback(() => {
    setIsLoading(true)
    getDealsContacts(() => {
      setIsLoading(false)
      if (selectedTemplate) {
        changeTemplate(selectedTemplate)
      }
    })
  }, [changeTemplate, getDealsContacts, selectedTemplate])

  useEffect(() => {
    if (dealRemoved) {
      getFollowupData()
    }
    setDealRemoved(false)
  }, [deals, dealRemoved, getFollowupData])

  const removeContact = contact => {
    const { contactId } = contact
    dispatch({
      type: ACT.REMOVE_CONTACT,
      payload: {
        contactId
      }
    })
  }

  const toggleContactDeal = (deal, contact) => {
    const isChecked = !deal.isChecked
    dispatch({
      type: ACT.TOGGLE_CONTACT_DEAL,
      payload: {
        id: deal.id,
        contactId: contact.contactId,
        isChecked
      }
    })

    const { template } = contact.email

    const filterCondition = d => {
      if (d.id === deal.id) {
        if (isChecked) return d
      } else {
        if (d.isChecked) return d
      }
    }

    template &&
      changeContactEmailTemplateBody(contact, template, [
        ...contact.dealsToFollowup.filter(d => filterCondition(d)).map(d => d.id),
        ...contact.otherDealsToFollowUp.filter(d => filterCondition(d)).map(d => d.id)
      ])
  }

  const setContactFollowUpDate = (contact, date) => {
    dispatch({
      type: ACT.SET_CONTACT_FOLLOWUP_DATE,
      payload: {
        contactId: contact.contactId,
        followUpDate: date
      }
    })
  }

  const setContactEmailSubject = (index, subject) => {
    dispatch({
      type: ACT.SET_CONTACT_EMAIL_SUBJECT,
      payload: {
        contactIndex: index,
        emailSubject: subject
      }
    })
  }

  const changeContactEmailTemplateBody = async (contact, template, dealsList) => {
    const response = await makePostRequest(
      `/v1/email/templates/contacts/${contact.contactId}/${template.value}/withselecteddeals`,
      dealsList
    )
    const index = contacts.findIndex(c => c.contactId === contact.contactId)
    setContactEmailTemplateBody(index, response.content)
    setContactEmailSubject(index, response.subject || response.title)
  }

  const setContactEmailTemplate = async (index, template) => {
    const contact = state.contacts[index]
    dispatch({
      type: ACT.SET_CONTACT_EMAIL_TEMPLATE,
      payload: {
        contactIndex: index,
        template
      }
    })

    changeContactEmailTemplateBody(contact, template, [
      ...contact.dealsToFollowup.filter(d => d.isChecked).map(d => d.id),
      ...contact.otherDealsToFollowUp.filter(d => d.isChecked).map(d => d.id)
    ])
  }

  const setContactEmailTemplateBody = (index, templateBody) => {
    dispatch({
      type: ACT.SET_CONTACT_EMAIL_TEMPLATE_BODY,
      payload: {
        contactIndex: index,
        templateBody
      }
    })
  }

  const setContactEmailFiles = (index, files) => {
    dispatch({
      type: ACT.SET_CONTACT_EMAIL_FILES,
      payload: {
        contactIndex: index,
        files
      }
    })
  }

  const removeFile = (fileName, index) => {
    dispatch({
      type: ACT.REMOVE_FILE,
      payload: {
        fileName,
        contactIndex: index
      }
    })
  }

  const sendIndividualEmail = async (contact, index) => {
    await makePostRequest('/v1/email/sendEmail2Contact/fordeals', toContactEmail(contact))

    dispatch({
      type: ACT.SET_CONTACT_EMAIL_SENT,
      payload: {
        contactIndex: index
      }
    })
  }

  const setFollowupDate = async (contact, index, cb) => {
    await makePutRequest('/v1/deals/followUpDateForMultipleDeals', toDealsFollowupData(contact))

    dispatch({
      type: ACT.SET_FOLLOW_UP_DATE,
      payload: {
        contactIndex: index
      }
    })

    cb && cb()
  }

  const setCCContacts = (index, ccContacts) => {
    dispatch({
      type: ACT.SET_CC_CONTACTS,
      payload: {
        ccContacts,
        contactIndex: index
      }
    })
  }

  return (
    <FollowupModal
      showFollowupModal={showFollowupModal}
      toggleFollowupModal={toggleFollowupModal}
      deals={deals}
      removeDeal={removeDeal}
      allContactsSelected={allContactsSelected}
      setAllContactsSelected={setAllContactsSelected}
      template={selectedTemplate}
      templates={templates}
      getFollowupData={getFollowupData}
      contacts={contacts}
      changeTemplate={changeTemplate}
      removeContact={removeContact}
      isLoading={isLoading}
      toggleContactDeal={toggleContactDeal}
      setContactFollowUpDate={setContactFollowUpDate}
      setContactEmailSubject={setContactEmailSubject}
      setContactEmailFiles={setContactEmailFiles}
      removeFile={removeFile}
      setContactEmailTemplate={setContactEmailTemplate}
      setContactEmailTemplateBody={setContactEmailTemplateBody}
      sendIndividualEmail={sendIndividualEmail}
      setFollowupDate={setFollowupDate}
      users={users}
      setCCContacts={setCCContacts}
    />
  )
}

export default FollowupModalContainer
